import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
  LscApiError,
  listLscNeedsAttentionMatchOptions,
  reassignLscNeedsAttentionMatchup,
} from '../api';
import NeedsAttentionActionConfirmModal from './NeedsAttentionActionConfirmModal';
import type {
  LscNeedsAttentionItem,
  LscNeedsAttentionMatchOption,
  LscNeedsAttentionMatchOptions,
} from '../types';
import { displaySeasonLabel } from '../seasonDisplay';

type NeedsAttentionReassignPanelProps = {
  leagueId: string;
  item: LscNeedsAttentionItem;
  onCancel: () => void;
  onDone: () => void;
};

const EMPTY_FILTERS = { seasons: [] as string[], weeks: [] as string[], teams: [] as string[] };

/**
 * Inline matchup reassignment: filterable schedule selection with likely
 * suggestions first, without navigating away from Needs Attention.
 */
const NeedsAttentionReassignPanel: React.FC<NeedsAttentionReassignPanelProps> = ({
  leagueId,
  item,
  onCancel,
  onDone,
}) => {
  const [options, setOptions] = useState<LscNeedsAttentionMatchOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);

  const [season, setSeason] = useState('');
  const [week, setWeek] = useState('');
  const [awayTeam, setAwayTeam] = useState('');
  const [homeTeam, setHomeTeam] = useState('');
  const [query, setQuery] = useState('');
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(
    item.competition_match_id ?? null
  );

  const isLiveException = Boolean(item.is_live_exception);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listLscNeedsAttentionMatchOptions(leagueId, item.item_key);
      setOptions(data);
    } catch (err) {
      setError(
        err instanceof LscApiError ? err.message : 'Could not load games for reassignment.'
      );
    } finally {
      setLoading(false);
    }
  }, [leagueId, item.item_key]);

  useEffect(() => {
    void load();
  }, [load]);

  const filters = options?.filters ?? EMPTY_FILTERS;

  const matchMatchesFilters = useCallback(
    (option: LscNeedsAttentionMatchOption): boolean => {
      if (season && (option.season_label ?? '') !== season) return false;
      if (week && (option.week_label ?? '') !== week) return false;
      if (awayTeam && (option.away_team ?? '').toLowerCase() !== awayTeam.toLowerCase())
        return false;
      if (homeTeam && (option.home_team ?? '').toLowerCase() !== homeTeam.toLowerCase())
        return false;
      if (query) {
        const haystack = [
          option.matchup_label,
          option.away_team,
          option.home_team,
          option.week_label,
          option.season_label,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query.toLowerCase())) return false;
      }
      return true;
    },
    [season, week, awayTeam, homeTeam, query]
  );

  const filteredMatches = useMemo(
    () => (options?.matches ?? []).filter(matchMatchesFilters),
    [options, matchMatchesFilters]
  );

  const suggestions = useMemo(
    () => (options?.suggestions ?? []).filter(matchMatchesFilters),
    [options, matchMatchesFilters]
  );

  const clearFilters = () => {
    setSeason('');
    setWeek('');
    setAwayTeam('');
    setHomeTeam('');
    setQuery('');
  };

  const submit = async (approve: boolean) => {
    if (!selectedMatchId) return;
    setSaving(true);
    setError(null);
    try {
      await reassignLscNeedsAttentionMatchup(leagueId, item.item_key, {
        competition_match_id: selectedMatchId,
        approve,
      });
      onDone();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not save the corrected matchup.');
      setSaving(false);
    }
  };

  const renderOption = (option: LscNeedsAttentionMatchOption) => {
    const selected = option.match_id === selectedMatchId;
    return (
      <li key={option.match_id}>
        <button
          type="button"
          onClick={() => setSelectedMatchId(option.match_id)}
          className={`w-full rounded-lg border p-3 text-left transition ${
            selected
              ? 'border-primary-400 bg-primary-500/10'
              : 'border-secondary-700 hover:border-secondary-500'
          }`}
          data-testid="reassign-match-option"
          aria-pressed={selected}
        >
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-white">{option.matchup_label}</span>
            {option.is_suggested ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-500/20 px-2 py-0.5 text-xs text-primary-200">
                <Sparkles className="h-3 w-3" aria-hidden />
                Likely match
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-secondary-400">
            {[option.week_label, displaySeasonLabel(option.season_label)].filter(Boolean).join(' · ') ||
              'Schedule details unavailable'}
          </p>
        </button>
      </li>
    );
  };

  return (
    <div
      className="rounded-lg border border-secondary-700 bg-secondary-900/40 p-4 space-y-4"
      data-testid="reassign-panel"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">
          {isLiveException ? 'Choose the correct game' : 'Reassign matchup'}
        </h3>
        <button
          type="button"
          className="text-xs text-secondary-400 hover:text-secondary-200"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>

      {error ? (
        <p className="text-sm text-red-300" data-testid="reassign-error">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-secondary-400">Loading games…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-xs text-secondary-400">
              Season
              <select
                className="input-field mt-1 w-full text-sm"
                value={season}
                onChange={(e) => setSeason(e.target.value)}
                data-testid="reassign-filter-season"
              >
                <option value="">All seasons</option>
                {filters.seasons.map((value) => (
                  <option key={value} value={value}>
                    {displaySeasonLabel(value)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-secondary-400">
              Week
              <select
                className="input-field mt-1 w-full text-sm"
                value={week}
                onChange={(e) => setWeek(e.target.value)}
                data-testid="reassign-filter-week"
              >
                <option value="">All weeks</option>
                {filters.weeks.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-secondary-400">
              Away team
              <select
                className="input-field mt-1 w-full text-sm"
                value={awayTeam}
                onChange={(e) => setAwayTeam(e.target.value)}
                data-testid="reassign-filter-away"
              >
                <option value="">Any away team</option>
                {filters.teams.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-secondary-400">
              Home team
              <select
                className="input-field mt-1 w-full text-sm"
                value={homeTeam}
                onChange={(e) => setHomeTeam(e.target.value)}
                data-testid="reassign-filter-home"
              >
                <option value="">Any home team</option>
                {filters.teams.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="search"
              className="input-field w-full text-sm"
              placeholder="Search games"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="reassign-filter-search"
            />
            <button
              type="button"
              className="text-xs text-secondary-400 hover:text-secondary-200 whitespace-nowrap"
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>

          {suggestions.length > 0 ? (
            <div className="space-y-2" data-testid="reassign-suggestions">
              <p className="text-xs uppercase tracking-wide text-secondary-500">
                Likely matches
              </p>
              <ul className="space-y-2">{suggestions.map(renderOption)}</ul>
            </div>
          ) : null}

          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-secondary-500">All games</p>
            {filteredMatches.length === 0 ? (
              <p className="text-sm text-secondary-400" data-testid="reassign-no-matches">
                No games match these filters.
              </p>
            ) : (
              <ul className="space-y-2 max-h-72 overflow-y-auto" data-testid="reassign-match-list">
                {filteredMatches.map(renderOption)}
              </ul>
            )}
          </div>

          {!selectedMatchId ? (
            <p className="text-xs text-secondary-400" data-testid="reassign-select-hint">
              Select a game first.
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={!selectedMatchId || saving}
              onClick={() => void submit(false)}
              data-testid="reassign-save"
            >
              {isLiveException ? 'Save selected game' : 'Save corrected matchup'}
            </button>
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={!selectedMatchId || saving}
              onClick={() => setConfirmApprove(true)}
              data-testid="reassign-save-approve"
            >
              {isLiveException ? 'Save & approve as live league game' : 'Save & approve to archive'}
            </button>
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={saving}
              onClick={onCancel}
            >
              Return to Needs Attention
            </button>
          </div>
        </>
      )}

      <NeedsAttentionActionConfirmModal
        open={confirmApprove}
        title="Save matchup and post?"
        body="This will save the selected matchup and publish the game to the league archive. Please confirm the matchup is correct."
        confirmLabel="Save and Post"
        busy={saving}
        onConfirm={() => {
          setConfirmApprove(false);
          void submit(true);
        }}
        onCancel={() => setConfirmApprove(false)}
      />
    </div>
  );
};

export default NeedsAttentionReassignPanel;
